import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Camera, ImageUp, ShieldCheck, Trash2 } from 'lucide-react';
import type { PayerPlanDto } from '@his/shared';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { Badge, Button, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons, type ChoiceOption } from '@/components/ui/choice-buttons';
import { RegistrationSection } from './RegistrationSection';
import { CARD_IMAGE_MAX_EDGE_PX, PHOTO_PICK_TYPES, downscaleImage, photoProblemText } from './patient-photo';
import { insuranceProblems, type InsuranceDraft } from './registration-extras';

type CoverChoice = 'none' | 'insured';

export function useRegistrationInsurance(): boolean {
  const { can, hasModule } = useAuth();
  return hasModule('insurance') && can('coverage.verify');
}

function CardImageField({ draft, onChange }: { draft: InsuranceDraft; onChange: (draft: InsuranceDraft) => void }) {
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const previewUrl = draft.card?.previewUrl ?? null;

  const take = async (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    setBusy(true);
    try {
      const blob = await downscaleImage(file, CARD_IMAGE_MAX_EDGE_PX);
      onChange({ ...draft, card: { blob, previewUrl: URL.createObjectURL(blob) } });
    } catch (error) {
      setProblem(photoProblemText(error));
    } finally {
      setBusy(false);
    }
  };

  const fileInput = (ref: typeof camera, capture: boolean) => (
    <input
      ref={ref}
      type="file"
      accept={capture ? 'image/*' : PHOTO_PICK_TYPES}
      capture={capture ? 'environment' : undefined}
      className="hidden"
      onChange={(event) => {
        void take(event.target.files?.[0]);
        event.target.value = '';
      }}
    />
  );

  return (
    <Field label={tr('opd', 'insurance_card_image')} hint={tr('opd', 'insurance_card_image_hint')} error={problem ?? undefined} className="sm:col-span-2">
      <div className="flex flex-wrap items-center gap-3">
        {previewUrl && <img src={previewUrl} alt={tr('opd', 'insurance_card_image')} className="h-16 w-24 rounded-md border object-cover" />}
        {fileInput(camera, true)}
        {fileInput(picker, false)}
        <Button type="button" variant="outline" size="sm" loading={busy} onClick={() => camera.current?.click()}>
          <Camera className="h-4 w-4" aria-hidden />
          {tr('opd', 'photo_take')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => picker.current?.click()}>
          <ImageUp className="h-4 w-4" aria-hidden />
          {tr('opd', 'photo_choose')}
        </Button>
        {draft.card && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...draft, card: null })}>
            <Trash2 className="h-4 w-4" aria-hidden />
            {tr('opd', 'insurance_card_remove')}
          </Button>
        )}
      </div>
    </Field>
  );
}

export function RegistrationInsurance({ draft, onChange }: { draft: InsuranceDraft; onChange: (draft: InsuranceDraft) => void }) {
  const { can } = useAuth();
  const payers = useQuery({
    queryKey: ['payers'],
    queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers'),
    enabled: draft.enabled,
    staleTime: 5 * 60_000,
  });
  const plans = useQuery({
    queryKey: ['payer-plans', draft.payerId],
    queryFn: () => get<PayerPlanDto[]>(`/insurance/payers/${draft.payerId}/plans`),
    enabled: draft.enabled && Boolean(draft.payerId),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const datesReversed = insuranceProblems(draft).includes('insurance_dates_reversed');
  const coverChoices: ChoiceOption<CoverChoice>[] = [
    { value: 'none', label: tr('opd', 'insurance_none') },
    { value: 'insured', label: tr('opd', 'insurance_add') },
  ];
  const activePlans = (plans.data ?? []).filter((plan) => plan.isActive);

  return (
    <RegistrationSection
      id="insurance"
      title={tr('opd', 'section_insurance')}
      description={tr('opd', 'section_insurance_hint')}
      icon={ShieldCheck}
      badge={<Badge variant="outline">{tr('opd', 'optional')}</Badge>}
    >
      <Field label={tr('opd', 'insurance_cover')} className="sm:col-span-2">
        <ChoiceButtons
          value={draft.enabled ? 'insured' : 'none'}
          onChange={(choice) => onChange({ ...draft, enabled: choice === 'insured' })}
          options={coverChoices}
          inline
        />
      </Field>
      {draft.enabled && (
        <>
          <Field label={tr('opd', 'insurance_payer')} required>
            <Select value={draft.payerId} onChange={(event) => onChange({ ...draft, payerId: event.target.value, planId: '' })}>
              <option value="">{tr('opd', 'insurance_choose_payer')}</option>
              {(payers.data ?? []).map((payer) => (
                <option key={payer.id} value={payer.id}>
                  {payer.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={tr('opd', 'insurance_plan')}>
            <Select value={draft.planId} disabled={!draft.payerId || plans.isError} onChange={(event) => onChange({ ...draft, planId: event.target.value })}>
              <option value="">{tr('opd', 'insurance_no_plan')}</option>
              {activePlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={tr('opd', 'insurance_member_number')} required>
            <Input value={draft.policyNo} maxLength={80} dir="ltr" autoComplete="off" onChange={(event) => onChange({ ...draft, policyNo: event.target.value })} />
          </Field>
          <div className="hidden sm:block" aria-hidden />
          <Field label={tr('opd', 'insurance_valid_from')}>
            <DatePicker value={draft.validFrom} onChange={(value) => onChange({ ...draft, validFrom: value })} />
          </Field>
          <Field label={tr('opd', 'insurance_valid_until')} error={datesReversed ? tr('opd', 'insurance_dates_reversed') : undefined}>
            <DatePicker value={draft.validTo} min={draft.validFrom || undefined} onChange={(value) => onChange({ ...draft, validTo: value })} />
          </Field>
          {can('patient.document.manage') && <CardImageField draft={draft} onChange={onChange} />}
        </>
      )}
    </RegistrationSection>
  );
}
